'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageWhyPropTradingSchema } from '@/sanity/pages/PageWhyPropTradingTemplate';
import dotsBg from '@/public/home/dots-bg-dark.png';

const ease = [0.25, 0.1, 0.25, 1] as const;

interface PropVsRow {
  _key?: string;
  feature?: string;
  col1?: string;
  col2?: string;
}

function PropVsTable({
  col1Label,
  col2Label,
  rows,
}: {
  col1Label: string;
  col2Label: string;
  rows: PropVsRow[];
}) {
  return (
    <div className="grid w-full min-w-[80rem] grid-cols-[16rem_1fr_1fr] overflow-hidden rounded-[1rem] lg:min-w-0 lg:grid-cols-[26.75rem_1fr_1fr]">
      {/* ── Header row ── */}
      {/* Feature col header — transparent spacer */}
      <div className="min-h-[8.875rem] rounded-tl-[1rem] backdrop-blur-[0.25rem]" />

      {/* Col 1 header */}
      <div className="flex min-h-[8.875rem] items-center rounded-tl-[1rem] bg-[rgba(255,255,255,0.10)] px-[3.5rem] py-[1.25rem] backdrop-blur-[0.4375rem]">
        <p className="mb-0 font-medium text-[1.75rem] leading-[1.5] tracking-[-0.09rem] text-white/90 lg:text-[2.25rem]">
          {col1Label}
        </p>
      </div>

      {/* Col 2 header */}
      <div className="flex min-h-[8.875rem] items-center rounded-tr-[1rem] border-l border-l-white/10 bg-[rgba(255,255,255,0.10)] px-[3.5rem] py-[1.25rem] backdrop-blur-[0.4375rem]">
        <p className="mb-0 font-medium text-[1.75rem] leading-[1.5] tracking-[-0.09rem] text-white/90 lg:text-[2.25rem]">
          {col2Label}
        </p>
      </div>

      {/* ── Data rows ── */}
      {rows.map((row, i) => {
        const isFirst = i === 0;
        const isLast = i === rows.length - 1;
        return (
          <React.Fragment key={row._key ?? i}>
            {/* Feature label */}
            <div
              className={[
                'flex min-h-[8.875rem] items-center border-b border-r border-b-white/10 border-r-white/10 bg-[rgba(255,255,255,0.08)] px-[2rem] py-[1.25rem] backdrop-blur-[0.5625rem] lg:px-[3.5rem]',
                isFirst ? 'rounded-tl-[1rem]' : '',
                isLast ? 'rounded-bl-[1rem]' : '',
              ].join(' ')}
            >
              <p className="mb-0 font-semibold text-[1.625rem] leading-[1.5] tracking-[-0.085rem] text-white lg:text-[2.125rem]">
                {row.feature}
              </p>
            </div>

            {/* Col 1 value */}
            <div className="flex min-h-[8.875rem] items-center border-b border-r border-b-white/10 border-r-white/10 bg-[rgba(255,255,255,0.12)] px-[3.5rem] py-[1.25rem] backdrop-blur-[0.5625rem]">
              <p className="mb-0 font-normal text-[1.625rem] leading-[1.5] tracking-[-0.085rem] text-white/60 lg:text-[2.125rem]">
                {row.col1}
              </p>
            </div>

            {/* Col 2 value */}
            <div
              className={[
                'flex min-h-[8.875rem] items-center border-b border-r border-b-white/10 border-r-white/10 bg-[rgba(255,255,255,0.12)] px-[3.5rem] py-[1.25rem] backdrop-blur-[0.5625rem]',
                isLast ? 'rounded-br-[1rem]' : '',
              ].join(' ')}
            >
              <p className="mb-0 font-normal text-[1.625rem] leading-[1.5] tracking-[-0.085rem] text-white/60 lg:text-[2.125rem]">
                {row.col2}
              </p>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function PropVsSelfFundedSection({
  data,
}: {
  data: PageWhyPropTradingSchema;
}) {
  if (!data?.propVsTitle && !data?.propVsRows?.length) return null;

  const col1 = data.propVsColumn1Label ?? 'Prop Trading with IQ Capital';
  const col2 = data.propVsColumn2Label ?? 'Trading Your Own Account';

  return (
    <section className="relative flex w-full flex-col items-center bg-[#04271d] px-[2rem] py-[8rem] lg:px-[6rem] lg:pb-[20rem] lg:pt-[15rem]">
      <div
        className="pointer-events-none absolute inset-0 opacity-60 mix-blend-exclusion"
        aria-hidden="true"
      >
        <Image src={dotsBg} alt="" className="h-full w-full object-cover" />
      </div>

      <div className="relative z-10 flex w-[min(140rem,100%)] flex-col gap-[6rem] lg:gap-[8rem]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[1.125rem] text-center"
        >
          {data?.propVsChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-white/70">
              {data.propVsChip}
            </p>
          )}
          {data?.propVsTitle && (
            <div className="richtext prose-h2:text-white prose-h2:text-[4rem] prose-h2:font-medium prose-h2:leading-[1.01] prose-h2:tracking-[-0.3rem] lg:prose-h2:text-[6rem]">
              <PortableText
                value={data.propVsTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {!!data?.propVsRows?.length && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="w-full overflow-x-auto prop-vs-scroll"
          >
            <PropVsTable
              col1Label={col1}
              col2Label={col2}
              rows={data.propVsRows}
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
